import type { ComputedRef } from 'vue'
import { computed } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import type { Instance } from '@openapi'
import { deepToRaw } from '@lib/util/reactive'
import type { RecentFile } from '@lib/types/recentFiles.types'
import {
  MAX_RECENT_FILES,
  RECENT_FILES_STORAGE_KEY_ADMIN,
  RECENT_FILES_STORAGE_KEY_IMPACT,
} from '@lib/constants/recentFiles.const'

export default function useRecentFiles({
  instanceId,
  app,
}: {
  instanceId: ComputedRef<Instance['id']>
  app: 'admin' | 'impact'
}) {
  // Store only minimal fields; drop everything else.
  // Shape: { [instanceId]: Array<{ id, name?, thumbnail_url?, tags?, page_index? }> }
  type StoredRecentFile = Pick<RecentFile, 'id' | 'name' | 'thumbnail_url' | 'tags' | 'page_index'>
  type StoredMap = Record<Instance['id'], StoredRecentFile[]>

  const instanceRecentFilesMap = useLocalStorage<StoredMap>(
    app === 'admin' ? RECENT_FILES_STORAGE_KEY_ADMIN : RECENT_FILES_STORAGE_KEY_IMPACT,
    {},
    {
      listenToStorageChanges: true,
      deep: true,
    },
  )

  // Backward-compatible migration to minimal shape
  ;(() => {
    const map = instanceRecentFilesMap.value || {}
    Object.keys(map).forEach((key) => {
      const items = Array.isArray(map[key]) ? (map[key] as any[]) : []
      const normalized: StoredRecentFile[] = items
        .map((item: any) => {
          if (!item) return null
          if (typeof item === 'string') return { id: item }
          else if (item && typeof item === 'object' && typeof (item as any).id === 'string') {
            const { id, name, thumbnail_url, tags, page_index } = item as any
            return { id, name, thumbnail_url, tags, page_index } as StoredRecentFile
          }
          return null
        })
        .filter(Boolean) as StoredRecentFile[]

      // Deduplicate by id, keep most recent order
      const seen = new Set<string>()
      const deduped: StoredRecentFile[] = []
      for (const f of normalized) {
        if (!seen.has(f.id)) {
          seen.add(f.id)
          deduped.push(f)
        }
      }
      map[key] = deduped.slice(0, MAX_RECENT_FILES)
    })
    instanceRecentFilesMap.value = map
  })()

  const instanceRecentFiles = computed(() =>
    (instanceRecentFilesMap.value[instanceId.value] ?? []).map(
      (stored) =>
        ({
          id: stored.id,
          name: stored.name,
          thumbnail_url: stored.thumbnail_url,
          tags: stored.tags,
          page_index: stored.page_index,
          instance_id: instanceId.value,
        }) as unknown as RecentFile,
    ),
  )

  function addRecentFile(file: RecentFile) {
    const filesForInstance = (instanceRecentFilesMap.value[instanceId.value] ?? []).filter((f) => f.id !== file.id)
    const minimal: StoredRecentFile = {
      id: file.id,
      name: file.name,
      thumbnail_url: file.thumbnail_url,
      tags: file.tags,
      page_index: file.page_index,
    }
    instanceRecentFilesMap.value[instanceId.value] = [minimal, ...filesForInstance].slice(0, MAX_RECENT_FILES)
  }

  function updateRecentFiles(files: Partial<RecentFile>[]): RecentFile[] {
    const fileIdsSet = new Set(files.map((file) => file.id))
    const recentFiles = instanceRecentFilesMap.value[instanceId.value]

    if (recentFiles) {
      const updatedFiles: StoredRecentFile[] = recentFiles.filter((recentFile) => fileIdsSet.has(recentFile.id))

      instanceRecentFilesMap.value[instanceId.value] = updatedFiles.map((recentFile) => {
        const fileToUpdate = files.find((f) => f.id === recentFile.id)
        return fileToUpdate
          ? {
              ...recentFile,
              thumbnail_url: fileToUpdate.thumbnail_url ?? recentFile.thumbnail_url,
              name: fileToUpdate.name ?? recentFile.name,
              tags: fileToUpdate.tags ?? recentFile.tags,
            }
          : recentFile
      })
    }

    return deepToRaw(
      (instanceRecentFilesMap.value[instanceId.value] ?? []).map((rf) => ({
        id: rf.id,
        name: rf.name,
        thumbnail_url: rf.thumbnail_url,
        tags: rf.tags,
        page_index: rf.page_index,
        instance_id: instanceId.value,
      })),
    ) as unknown as RecentFile[]
  }

  function updatePageInRecentFile(fileId: string, pageIndex: number) {
    const file = instanceRecentFilesMap.value[instanceId.value].find((f) => f.id === fileId)

    if (!file) {
      return
    }

    instanceRecentFilesMap.value[instanceId.value] = instanceRecentFilesMap.value[instanceId.value].map((recentFile) =>
      recentFile.id === fileId ? { ...recentFile, page_index: pageIndex } : recentFile,
    )
  }

  return { addRecentFile, instanceRecentFiles, updateRecentFiles, updatePageInRecentFile }
}
